import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '@/components/navigation/navbar';
import { Footer } from '@/components/navigation/footer';
import { MobileDrawer } from '@/components/navigation/mobile-drawer';
import { MobileBottomNav } from '@/components/navigation/mobile-bottom-nav';
import { SJAnnouncementBar } from '@/components/layout/sj-announcement-bar';
import { GlobalCommandMenu } from '@/components/search/global-command-menu';
import { AskSanjoyDrawer } from '@/components/ask';
import { useSearchStore } from '@/store/search';
import { ROUTES } from '@/utils/constants';
import { useScrollToTop, useDocumentTitle, useReducedMotion } from '@/hooks';
import { ensureAskSanjoyWidget } from '@/utils';
import { cn } from '@/utils';

export function RootLayout() {
  const location = useLocation();
  const { open: openSearch } = useSearchStore();
  const prefersReducedMotion = useReducedMotion();
  const [routeAnnouncement, setRouteAnnouncement] = useState('');
  const hideTabBar = location.pathname === ROUTES.LOGIN;

  useScrollToTop();
  useDocumentTitle();

  useEffect(() => {
    void ensureAskSanjoyWidget().catch(() => {
      /* CSP/network errors are reported when the user opens Ask Sanjoy */
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRouteAnnouncement(document.title);
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[300] focus:rounded-[8px] focus:bg-[var(--sj-surface)] focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-[var(--sj-text-primary)] focus:shadow-lg focus:border focus:border-[var(--sj-border)]"
      >
        Skip to main content
      </a>

      <SJAnnouncementBar />
      <Navbar />
      <MobileDrawer />

      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          id="main-content"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? undefined : { opacity: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className={cn('flex-1', !hideTabBar && 'pb-tab-bar')}
        >
          <Outlet />
        </motion.div>
      </AnimatePresence>

      <Footer />
      <MobileBottomNav />
      <GlobalCommandMenu />
      <AskSanjoyDrawer />
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {routeAnnouncement}
      </div>
    </div>
  );
}
